import { useSyncExternalStore } from "react";

const WELCOME_KEY = "neca.phone.welcome.v1";
const memory = new Map<string, string>();
function read(key: string) {
  try { return localStorage.getItem(key) ?? memory.get(key); } catch { return memory.get(key); }
}
function write(key: string, value: string) {
  try { localStorage.setItem(key, value); memory.delete(key); }
  catch { memory.set(key, value); }
}
export function hasSeenWelcome() { return read(WELCOME_KEY) === "1"; }
export function markWelcomeSeen() { write(WELCOME_KEY, "1"); }
export function phonePlatform(agent = navigator.userAgent): "ios" | "android" | "desktop" {
  if (/iPhone|iPod/i.test(agent)) return "ios";
  return /Android/i.test(agent) ? "android" : "desktop";
}
export function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
export function pushSupported() {
  return window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}
export function rememberDevice(userId: string, endpoint: string, id: string) {
  write(`neca.phone.push.${userId}`, JSON.stringify({ endpoint, id }));
}
export async function currentDeviceActive(userId: string, ids: string[]) {
  if (!pushSupported() || Notification.permission !== "granted") return false;
  const saved = read(`neca.phone.push.${userId}`);
  if (!saved) return false;
  try {
    const device = JSON.parse(saved) as { endpoint: string; id: string };
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    return subscription?.endpoint === device.endpoint && ids.includes(device.id);
  } catch { return false; }
}

type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };
let installPrompt: InstallPrompt | null = null;
const listeners = new Set<() => void>();
function changed() { listeners.forEach(listener => listener()); }
// Importado por main antes del primer render: el evento también se conserva durante login y cambio de clave.
window.addEventListener("beforeinstallprompt", event => {
  event.preventDefault();
  installPrompt = event as InstallPrompt;
  changed();
});
window.addEventListener("appinstalled", () => { installPrompt = null; changed(); });
export function useInstallPrompt() {
  return useSyncExternalStore(listener => { listeners.add(listener); return () => { listeners.delete(listener); }; }, () => installPrompt);
}
export async function installApp() {
  const event = installPrompt;
  if (!event) return null;
  installPrompt = null;
  changed();
  await event.prompt();
  return (await event.userChoice).outcome;
}
